import { Link, useNavigate, useParams } from 'react-router-dom';
import { normalizeKindId } from '@gm-binder/shared';
import { Card, IconButton, EmptyState } from '../../components/ui';
import { GenericDetail } from '../../components/views/Generic';
import { EntityForm } from '../../components/views/EntityForm';
import { Backlinks } from '../../components/common/Backlinks';
import { useEditorStore } from '../../store/editorStore';
import { deleteEntityFrom, listEntitiesOf, updateEntityIn } from '../../lib/entityBuckets';
import { entityTitle, resolveKindDef } from '../../lib/viewRegistry';
import styles from './EntityPages.module.css';

/** Detail editor: field form on the left, live generic preview on the right. */
export function EntityEditPage() {
  const { slug = '', sectionId = '', entityId = '' } = useParams();
  const navigate = useNavigate();
  const adventure = useEditorStore((s) => s.adventure);
  const update = useEditorStore((s) => s.update);
  if (!adventure) return null;

  const section = adventure.sections.find((s) => s.id === sectionId);
  if (!section) return <EmptyState icon="📄" title="Sezione non trovata" />;

  const kindId = normalizeKindId(section.kind ?? section.type ?? 'custom');
  const kindDef = resolveKindDef(kindId, adventure.kinds as Parameters<typeof resolveKindDef>[1]);
  const entity = listEntitiesOf(adventure, kindId).find((e) => String(e.id) === entityId);

  if (!entity) return <EmptyState icon="📄" title="Voce non trovata" />;

  const listHref = `/library/${slug}/edit/${sectionId}`;

  const onDelete = () => {
    if (!confirm(`Eliminare "${entityTitle(kindDef, entity)}"?`)) return;
    update((a) => deleteEntityFrom(a, kindId, entityId));
    navigate(listHref);
  };

  return (
    <div className={styles.stack}>
      <div className={styles.detailHeader}>
        <Link to={listHref} className={styles.back}>
          ← {section.title}
        </Link>
        <div className={styles.spacer} />
        <IconButton label="Elimina voce" onClick={onDelete}>
          🗑️
        </IconButton>
      </div>

      <h2 className={styles.entityTitle}>
        <span aria-hidden>{kindDef.icon}</span> {entityTitle(kindDef, entity)}
      </h2>

      <div className={styles.editGrid}>
        <Card>
          <EntityForm
            kind={kindDef}
            value={entity}
            adventure={adventure}
            slug={slug}
            onChange={(next) => update((a) => updateEntityIn(a, kindId, entityId, next))}
          />
        </Card>

        <div className={styles.previewCol}>
          <div className={styles.previewLabel}>Anteprima</div>
          <Card>
            <GenericDetail kind={kindDef} entity={entity} slug={slug} adventure={adventure} />
          </Card>
          <Backlinks adventure={adventure} targetRef={`${kindId}:${entityId}`} />
        </div>
      </div>
    </div>
  );
}
